import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import VisualOutputStyleFields from './VisualOutputStyleFields';
import {
  AGENT_RELEVANCE_MODE_OPTIONS,
  DEFAULT_INTERACTIVE_LIST_BUTTON_LABEL,
  SESSION_CLOSE_POLICY_OPTIONS,
} from '../constants';

function AgentOperationSettings({
  sessionClosePolicy,
  setSessionClosePolicy,
  sessionTimeoutHours,
  setSessionTimeoutHours,
  escalationCooldownHours,
  setEscalationCooldownHours,
  knowledgeRelevanceMode,
  setKnowledgeRelevanceMode,
  knowledgeFileButtonEnabled,
  setKnowledgeFileButtonEnabled,
  interactiveOptionsEnabled,
  setInteractiveOptionsEnabled,
  interactiveListButtonLabel,
  setInteractiveListButtonLabel,
  visualOutputEnabled,
  setVisualOutputEnabled,
  visualOutputFormats,
  setVisualOutputFormats,
  visualOutputStyleInstructions,
  setVisualOutputStyleInstructions,
  visualOutputAssets,
  setVisualOutputAssets,
  agentId = null,
  requireRegisteredContact,
  setRequireRegisteredContact,
  showAccessSettings = true,
  disabled = false,
}) {
  return (
    <div className="space-y-4">
      <Select
        id="session_close_policy"
        label="Encerramento da sessão"
        value={sessionClosePolicy}
        onChange={(e) => setSessionClosePolicy(e.target.value)}
        options={SESSION_CLOSE_POLICY_OPTIONS}
        hint="Escolha se a sessão acompanha o tempo de inatividade ou representa apenas uma requisição e sua resposta."
      />

      {sessionClosePolicy !== 'per_interaction' && (
        <Input
          id="session_timeout"
          label="Timeout de sessão (horas)"
          type="number"
          value={sessionTimeoutHours}
          onChange={(e) => setSessionTimeoutHours(e.target.value)}
          placeholder="4"
          hint="Tempo de inatividade antes de encerrar a sessão do usuário."
        />
      )}

      <Input
        id="escalation_cooldown"
        label="Pausa após transbordo (horas)"
        type="number"
        min="0"
        step="1"
        value={escalationCooldownHours}
        onChange={(e) => setEscalationCooldownHours(e.target.value)}
        placeholder="24"
        hint="Tempo em que a IA fica pausada após passar o atendimento para um humano. Use 0 para voltar na próxima mensagem do cliente."
      />

      <Select
        id="relevance_mode"
        label="Modo de relevância (knowledge)"
        value={knowledgeRelevanceMode}
        onChange={(e) => setKnowledgeRelevanceMode(e.target.value)}
        options={AGENT_RELEVANCE_MODE_OPTIONS}
        hint="Define o rigor da busca nas bases de conhecimento vinculadas ao agente."
      />

      <label className="flex items-center gap-2 text-sm text-content-secondary">
        <input
          type="checkbox"
          checked={knowledgeFileButtonEnabled}
          onChange={(e) => setKnowledgeFileButtonEnabled(e.target.checked)}
        />
        Oferecer arquivo original quando usar knowledge
      </label>
      <p className="text-xs text-content-muted -mt-2">
        Quando ativo, o agente envia um botão para o usuário consultar o arquivo de origem
        usado na resposta.
      </p>

      <label className="flex items-center gap-2 text-sm text-content-secondary">
        <input
          type="checkbox"
          checked={interactiveOptionsEnabled}
          onChange={(e) => {
            const checked = e.target.checked;
            setInteractiveOptionsEnabled(checked);
            if (checked && !String(interactiveListButtonLabel || '').trim()) {
              setInteractiveListButtonLabel(DEFAULT_INTERACTIVE_LIST_BUTTON_LABEL);
            }
          }}
        />
        Enviar listas de opções em botões interativos
      </label>
      <p className="text-xs text-content-muted -mt-2">
        Quando ativo, se a resposta tiver uma lista de 2 a 10 opções para o cliente escolher,
        o texto segue na mensagem e as opções abrem numa lista interativa do WhatsApp.
      </p>
      {interactiveOptionsEnabled && (
        <Input
          id="interactive_list_button_label"
          label="Texto do botão que abre a lista"
          value={interactiveListButtonLabel}
          onChange={(e) => setInteractiveListButtonLabel(e.target.value.slice(0, 20))}
          placeholder={DEFAULT_INTERACTIVE_LIST_BUTTON_LABEL}
          hint="O cliente toca neste botão para ver as opções. Até 20 caracteres."
        />
      )}

      <label className="flex items-center gap-2 text-sm text-content-secondary">
        <input
          type="checkbox"
          checked={visualOutputEnabled}
          onChange={(e) => setVisualOutputEnabled(e.target.checked)}
        />
        Permitir geração de imagem/PDF (saída visual A4)
      </label>
      <p className="text-xs text-content-muted -mt-2">
        Quando ativo, o agente pode gerar PNG, JPG ou PDF via OpenAI quando o usuário pedir
        explicitamente um material visual.
      </p>

      {visualOutputEnabled && (
        <div className="space-y-2 pl-1">
          <p className="text-xs font-medium text-content-secondary">Formatos permitidos</p>
          {[
            { value: 'png', label: 'PNG' },
            { value: 'jpg', label: 'JPG' },
            { value: 'pdf', label: 'PDF' },
          ].map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-2 text-sm text-content-secondary"
            >
              <input
                type="checkbox"
                checked={visualOutputFormats.includes(option.value)}
                onChange={(e) => {
                  setVisualOutputFormats((current) => {
                    if (e.target.checked) {
                      return current.includes(option.value)
                        ? current
                        : [...current, option.value];
                    }
                    const next = current.filter((item) => item !== option.value);
                    return next.length ? next : ['png'];
                  });
                }}
              />
              {option.label}
            </label>
          ))}
          <VisualOutputStyleFields
            visualOutputStyleInstructions={visualOutputStyleInstructions}
            setVisualOutputStyleInstructions={setVisualOutputStyleInstructions}
            visualOutputAssets={visualOutputAssets}
            setVisualOutputAssets={setVisualOutputAssets}
            agentId={agentId}
            disabled={disabled}
          />
        </div>
      )}

      {showAccessSettings && (
        <>
          <label className="flex items-center gap-2 text-sm text-content-secondary">
            <input
              type="checkbox"
              checked={requireRegisteredContact}
              onChange={(e) => setRequireRegisteredContact(e.target.checked)}
            />
            Atender apenas números já cadastrados
          </label>
          <p className="text-xs text-content-muted -mt-2">
            Quando ativo, o agente ignora mensagens de external_id sem contato vinculado. Por
            padrão, novos números são atendidos automaticamente.
          </p>
        </>
      )}
    </div>
  );
}

export default AgentOperationSettings;
